import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth, db } from '@/lib/firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ADMIN_EMAILS } from '@/lib/defaults';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkRole = useCallback(async (email) => {
    if (ADMIN_EMAILS.includes(email.toLowerCase())) return 'admin';
    try {
      const adminSnap = await getDocs(query(collection(db, 'admins'), where('email', '==', email.toLowerCase())));
      if (!adminSnap.empty) {
        const adminDoc = adminSnap.docs[0].data();
        if (adminDoc.enabled !== false) return 'admin';
      }
      const studentSnap = await getDocs(query(collection(db, 'students'), where('email', '==', email.toLowerCase())));
      if (!studentSnap.empty) {
        const studentDoc = studentSnap.docs[0].data();
        if (studentDoc.enabled !== false) return 'student';
      }
    } catch (e) {
      console.error('Role check error:', e);
    }
    if (ADMIN_EMAILS.includes(email.toLowerCase())) return 'admin';
    return null;
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const r = await checkRole(firebaseUser.email);
        setRole(r);
      } else {
        setUser(null);
        setRole(null);
      }
      setLoading(false);
    });
    return unsub;
  }, [checkRole]);

  const checkEmailAllowed = async (email) => {
    if (ADMIN_EMAILS.includes(email.toLowerCase())) return 'admin';
    try {
      const adminSnap = await getDocs(query(collection(db, 'admins'), where('email', '==', email.toLowerCase())));
      if (!adminSnap.empty && adminSnap.docs[0].data().enabled !== false) return 'admin';
      const studentSnap = await getDocs(query(collection(db, 'students'), where('email', '==', email.toLowerCase())));
      if (!studentSnap.empty && studentSnap.docs[0].data().enabled !== false) return 'student';
    } catch (e) {
      console.error('Email check error:', e);
    }
    return null;
  };

  const login = async (email, password) => {
    const userCred = await signInWithEmailAndPassword(auth, email, password);
    const r = await checkRole(userCred.user.email);
    setRole(r);
    return { user: userCred.user, role: r };
  };

  const register = async (email, password) => {
    const userCred = await createUserWithEmailAndPassword(auth, email, password);
    const r = await checkRole(userCred.user.email);
    setRole(r);
    await setDoc(doc(db, 'users', userCred.user.uid), {
      email: email.toLowerCase(),
      role: r,
      createdAt: new Date().toISOString(),
    });
    return { user: userCred.user, role: r };
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setRole(null);
  };

  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  return (
    <AuthContext.Provider value={{ user, role, loading, login, register, logout, resetPassword, checkEmailAllowed }}>
      {children}
    </AuthContext.Provider>
  );
}
